import React from 'react';
import { useTranslation } from '../hooks/useTranslation';

const PlatformText = ({ 
  children, 
  variant = 'body',
  size = 'medium',
  weight = 'normal',
  color = 'default',
  align = 'left',
  className = '',
  style = {},
  // i18n props
  translationKey = null,
  namespace = 'common',
  translationOptions = {},
  // If true, children will be used as fallback if translation fails
  fallbackToChildren = true,
  ...props 
}) => {
  const { t, isHindi } = useTranslation(namespace);
  
  // Get the text content - either translated or children
  const getTextContent = () => {
    if (translationKey) {
      const translatedText = t(translationKey, translationOptions);
      // If translation fails and we have fallback enabled, use children
      if (translatedText === translationKey && fallbackToChildren && children) {
        return children;
      }
      return translatedText;
    }
    return children;
  };

  const textContent = getTextContent();

  // Get the appropriate HTML element
  const getElement = () => {
    switch (variant) {
      case 'h1': return 'h1';
      case 'h2': return 'h2';
      case 'h3': return 'h3';
      case 'h4': return 'h4';
      case 'h5': return 'h5';
      case 'h6': return 'h6';
      case 'button': return 'span';
      default: return 'p';
    }
  };

  const Element = getElement();

  // Build CSS class name for ALL platforms using token-based classes
  const getClassName = () => {
    const baseClass = variant === 'body' ? 'text-body' : `text-${variant}`;
    const sizeClass = size !== 'medium' ? `size-${size}` : '';
    const colorClass = color !== 'default' ? `color-${color}` : '';
    const alignClass = align !== 'left' ? `align-${align}` : '';
    
    return [baseClass, sizeClass, colorClass, alignClass, className]
      .filter(Boolean)
      .join(' ');
  };

  // Minimal inline styles for ALL platforms
  const getInlineStyles = () => {
    const styles = {};
    
    // Only add platform-specific styles that CANNOT be handled by CSS
    if (isHindi()) {
      styles.fontFamily = '"Noto Sans Devanagari", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';
    }
    
    return { ...styles, ...style };
  };

  return (
    <Element 
      className={getClassName()}
      style={getInlineStyles()}
      {...props}
    >
      {textContent}
    </Element>
  );
};

export default PlatformText; 